import type { RemoveRiskFromEnterpriseRiskMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

// Maps the tRPC `{ affectedRows }` response to the GraphQL mutation shape so the
// flag-gated wrapper can return one type regardless of which path ran.
const mapTrpcResponseToGraphQL = (
  affectedRows: number
): RemoveRiskFromEnterpriseRiskMutation => ({
  removeRiskFromEnterpriseRisk: {
    __typename: 'GenericMutationOutput',
    affected_rows: affectedRows,
  },
});

/**
 * tRPC implementation of the remove-risk-from-enterprise-risk mutation
 * (COI-432). NOT imported outside this directory — consumers use the flag-gated
 * `useRemoveRiskFromEnterpriseRisk` wrapper. Cache-invalidation parity with the
 * Apollo twin's `evictField` set.
 */
export const useRemoveRiskFromEnterpriseRiskTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.frontend.enterpriseRisk.unlink.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getByTier.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.riskById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.register.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    removeRiskFromEnterpriseRisk: async (
      riskId: string
    ): Promise<RemoveRiskFromEnterpriseRiskMutation> => {
      const result = await mutation.mutateAsync({ RiskId: riskId });

      return mapTrpcResponseToGraphQL(result.affectedRows);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
